import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import { useLocationContext } from "@/contexts/LocationContext";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  startOfYear,
  subDays,
  subWeeks,
  subMonths,
  format,
  eachWeekOfInterval,
  eachMonthOfInterval,
} from "date-fns";
import { convertCurrencyAmount } from "@/lib/fx-rates";

interface AnalyticsData {
  // Key metrics
  bookingsThisWeek: number;
  bookingsThisMonth: number;
  revenueThisMonth: number; // Appointments revenue
  invoiceRevenueThisMonth: number; // Invoice revenue
  walkInRevenueThisMonth: number; // Walk-in/day pass revenue
  membershipRevenueThisMonth: number; // Membership renewal revenue
  totalRevenueThisMonth: number; // Total revenue (appointments + invoices + walk-ins + memberships)
  noShowRate: number;
  
  // Time series data
  revenueOverTime: Array<{ date: string; revenue: number }>;
  appointmentsOverTime: Array<{ date: string; count: number }>;
  
  // Service popularity
  servicePopularity: Array<{ name: string; count: number; revenue: number }>;
  
  // Status distribution
  statusDistribution: Array<{ status: string; count: number }>;
  
  // Weekly trends
  weeklyTrends: Array<{ week: string; bookings: number; revenue: number }>;

  // Weekday trends (for "Weekly/Monthly" toggle UI)
  weekdayTrendsWeek: Array<{ day: string; count: number }>;
  monthlyTrends6Months: Array<{ month: string; count: number }>;

  // Service performance
  servicePerformance: Array<{
    serviceId: string;
    serviceName: string;
    bookings: number;
    revenue: number;
    avgPrice: number;
    completionRate: number; // %
  }>;

  // Staff performance
  staffSummary: {
    activeProviders: number;
    topPerformerName: string | null;
    topPerformerRevenue: number;
    avgCompletionRate: number; // %
  };
  staffPerformance: Array<{
    staffId: string; // "unassigned" possible
    staffName: string;
    bookings: number;
    revenue: number;
    completionRate: number; // %
    hours: number; // from appointment durations
    hoursWorkedFromClock: number; // from clock in/out pairs this month
    specialties: string[];
    clockInsCount: number;
    lastClockAt: string | null;
  }>;

  // Export-ready rows (current month)
  exportAppointments: Array<{
    booked_at: string;
    appointment_start: string;
    appointment_end: string;
    customer_name: string;
    customer_email: string;
    service_name: string;
    staff_name: string;
    status: string;
    total_price_amount: number;
    currency: string;
    addons: string;
    source: string;
    confirmation_code: string;
  }>;
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export type UseAnalyticsOptions = {
  /** Filter by service category id; use "uncategorized" for services with no category; null = all */
  categoryId?: string | null;
  /** Report period. Defaults to the current month. */
  from?: Date;
  to?: Date;
};

export function useAnalytics(options?: UseAnalyticsOptions) {
  const { business } = useBusiness();
  const { selectedLocationId } = useLocationContext();
  const categoryId = options?.categoryId ?? null;
  const optionFrom = options?.from;
  const optionTo = options?.to;
  const optionFromTime = optionFrom?.getTime() ?? null;
  const optionToTime = optionTo?.getTime() ?? null;
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [data, setData] = useState<AnalyticsData>({
    bookingsThisWeek: 0,
    bookingsThisMonth: 0,
    revenueThisMonth: 0,
    invoiceRevenueThisMonth: 0,
    walkInRevenueThisMonth: 0,
    membershipRevenueThisMonth: 0,
    totalRevenueThisMonth: 0,
    noShowRate: 0,
    revenueOverTime: [],
    appointmentsOverTime: [],
    servicePopularity: [],
    statusDistribution: [],
    weeklyTrends: [],
    weekdayTrendsWeek: [],
    monthlyTrends6Months: [],
    servicePerformance: [],
    staffSummary: {
      activeProviders: 0,
      topPerformerName: null,
      topPerformerRevenue: 0,
      avgCompletionRate: 0,
    },
    staffPerformance: [],
    exportAppointments: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!business) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const now = new Date();
      const yearStart = startOfYear(now);
      const reportStartDate = optionFrom ? startOfDay(optionFrom) : startOfMonth(now);
      const reportEndDate = optionTo ? endOfDay(optionTo) : endOfMonth(now);
      const weekStart = startOfWeek(now, { weekStartsOn: 1 }).toISOString();
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 }).toISOString();
      const monthStart = reportStartDate.toISOString();
      const monthEnd = reportEndDate.toISOString();
      const rangeStart = new Date(Math.min(yearStart.getTime(), reportStartDate.getTime())).toISOString();

      // Fetch service categories for filter dropdown
      const { data: categoriesRows } = await supabase
        .from("service_categories")
        .select("id, name")
        .eq("business_id", business.id)
        .order("position", { ascending: true })
        .order("name", { ascending: true });
      setCategories((categoriesRows || []).map((c: any) => ({ id: c.id, name: c.name || "" })));

      // Fetch appointments for analytics range (last ~120 days)
      let appointmentsQuery = supabase
        .from("appointments")
        .select(`
          *,
          customer:customers(name, email),
          service:services(id, name, price_amount, currency, category_id),
          staff:staff(id, name)
        `)
        .eq("business_id", business.id)
        .gte("start_datetime", rangeStart);
      
      // Filter by location if selected
      if (selectedLocationId) {
        appointmentsQuery = appointmentsQuery.eq("location_id", selectedLocationId);
      }
      
      const { data: appointmentsRaw, error: appError } = await appointmentsQuery.order("start_datetime", { ascending: true });

      if (appError) throw appError;

      // Apply category filter
      let appointments = appointmentsRaw || [];
      if (categoryId) {
        if (categoryId === "uncategorized") {
          appointments = appointments.filter((a: any) => !(a.service as any)?.category_id);
        } else {
          appointments = appointments.filter((a: any) => (a.service as any)?.category_id === categoryId);
        }
      }

      // Calculate key metrics
      const bookingsThisWeek =
        appointments?.filter((a) => a.start_datetime >= weekStart && a.start_datetime <= weekEnd)
          .length || 0;

      const bookingsThisMonth =
        appointments?.filter((a) => a.start_datetime >= monthStart && a.start_datetime <= monthEnd)
          .length || 0;

      const revenueEligibleStatuses = new Set([
        "pending",
        "confirmed",
        "completed",
        "rescheduled",
        "in_progress",
      ]);

      const monthAppointmentsAll =
        appointments?.filter((a) => a.start_datetime >= monthStart && a.start_datetime <= monthEnd) ||
        [];
      const displayCurrency = business.currency || "GHS";

      const netAppointmentTotal = (a: any) => {
        const total = Number(a?.total_price_amount);
        if (Number.isFinite(total) && total > 0) return total;
        const servicePrice = parseFloat(a?.service?.price_amount?.toString() || "0") || 0;
        const discount = Number(a?.discount_amount || 0);
        return Math.max(0, servicePrice - discount);
      };
      const appointmentCurrency = (a: any) =>
        String(a?.currency || a?.service?.currency || displayCurrency);
      const convertRevenue = (amount: number, sourceCurrency?: string | null) =>
        convertCurrencyAmount(amount, sourceCurrency || displayCurrency, displayCurrency);
      const displayAppointmentTotal = (a: any) => convertRevenue(netAppointmentTotal(a), appointmentCurrency(a));

      // Booked revenue (exclude cancelled/no_show). This fixes "no data" when you haven't marked many as completed yet.
      const revenueThisMonth = monthAppointmentsAll
        .filter((a) => revenueEligibleStatuses.has(a.status))
        .reduce(
          (sum, a: any) =>
            sum +
            displayAppointmentTotal(a),
          0
        );

      const totalAppointments = appointments?.length || 0;
      const noShows = appointments?.filter((a) => a.status === "no_show").length || 0;
      const noShowRate = totalAppointments > 0 ? (noShows / totalAppointments) * 100 : 0;

      // Fetch invoice revenue (paid invoices this month)
      const { data: invoices, error: invError } = await (supabase as any)
        .from("invoices")
        .select(`
          id,
          currency,
          invoice_items(id, quantity, unit_price),
          invoice_adjustments(id, type, mode, amount, percent_rate, included_in_prices)
        `)
        .eq("business_id", business.id)
        .eq("status", "paid")
        .gte("paid_at", monthStart)
        .lte("paid_at", monthEnd);

      if (invError) throw invError;

      // Calculate invoice revenue (items + adjustments)
      let invoiceRevenueThisMonth = 0;
      if (invoices) {
        for (const inv of invoices) {
          // Sum line items
          const itemsTotal = (inv.invoice_items || []).reduce(
            (sum: number, item: any) => sum + Number(item.quantity || 0) * Number(item.unit_price || 0),
            0
          );

          // Apply adjustments
          let adjustedTotal = itemsTotal;
          for (const adj of inv.invoice_adjustments || []) {
            if (adj.type === "tax" && adj.included_in_prices) {
              // Tax is already included, don't add it
              continue;
            }
            if (adj.mode === "percent") {
              const percentValue = Number(adj.percent_rate || 0);
              if (adj.type === "tax") {
                adjustedTotal += (itemsTotal * percentValue) / 100;
              } else if (adj.type === "discount") {
                adjustedTotal -= (itemsTotal * percentValue) / 100;
              }
            } else {
              // Fixed amount
              if (adj.type === "tax") {
                adjustedTotal += Number(adj.amount || 0);
              } else if (adj.type === "discount") {
                adjustedTotal -= Number(adj.amount || 0);
              }
            }
          }

          invoiceRevenueThisMonth += convertRevenue(adjustedTotal, (inv as any).currency);
        }
      }

      // Fetch walk-in revenue (day passes this month)
      const { data: dayPasses, error: dpError } = await (supabase as any)
        .from("day_passes")
        .select("amount, currency, payment_status, created_at")
        .eq("business_id", business.id)
        .eq("payment_status", "paid")
        .gte("created_at", monthStart)
        .lte("created_at", monthEnd);

      if (dpError) throw dpError;

      const walkInRevenueThisMonth = (dayPasses || []).reduce(
        (sum: number, dp: any) => sum + convertRevenue(Number(dp.amount || 0), dp.currency),
        0
      );

      // Fetch membership renewal revenue (membership_payment context this month)
      const { data: membershipPayments, error: mpError } = await (supabase as any)
        .from("platform_payments")
        .select("gross_amount, currency, paid_at")
        .eq("business_id", business.id)
        .eq("context", "membership_payment")
        .eq("status", "success")
        .gte("paid_at", monthStart)
        .lte("paid_at", monthEnd);

      if (mpError) throw mpError;

      const membershipRevenueThisMonth = (membershipPayments || []).reduce(
        (sum: number, p: any) => sum + convertRevenue(Number(p.gross_amount || 0), p.currency),
        0
      );

      // Calculate total revenue
      const totalRevenueThisMonth = revenueThisMonth + invoiceRevenueThisMonth + walkInRevenueThisMonth + membershipRevenueThisMonth;

      // Revenue + appointment counts over time (rolling weekly buckets, last 12 weeks through current week)
      const revenueWindowEnd = endOfWeek(now, { weekStartsOn: 1 });
      const revenueWindowStart = startOfWeek(subWeeks(now, 11), { weekStartsOn: 1 });
      const weeksRevenueWindow = eachWeekOfInterval(
        { start: revenueWindowStart, end: revenueWindowEnd },
        { weekStartsOn: 1 }
      );

      const revenueOverTime = weeksRevenueWindow.map((week) => {
        const ws = startOfWeek(week, { weekStartsOn: 1 });
        const we = endOfWeek(week, { weekStartsOn: 1 });
        const wsIso = ws.toISOString();
        const weIso = we.toISOString();
        const weekAppointments = (appointments || []).filter(
          (a) => a.start_datetime >= wsIso && a.start_datetime <= weIso && revenueEligibleStatuses.has(a.status)
        );
        const revenue = weekAppointments.reduce((sum, a: any) => sum + displayAppointmentTotal(a), 0);
        return { date: format(ws, "MMM d"), revenue };
      });

      const appointmentsOverTime = weeksRevenueWindow.map((week) => {
        const ws = startOfWeek(week, { weekStartsOn: 1 });
        const we = endOfWeek(week, { weekStartsOn: 1 });
        const wsIso = ws.toISOString();
        const weIso = we.toISOString();
        const count =
          (appointments || []).filter((a) => a.start_datetime >= wsIso && a.start_datetime <= weIso).length || 0;
        return { date: format(ws, "MMM d"), count };
      });

      // Service popularity
      const serviceMap = new Map<string, { count: number; revenue: number }>();
      appointments?.forEach((apt) => {
        const serviceName = apt.service?.name || "Unknown";
        const price = displayAppointmentTotal(apt);
        
        if (!serviceMap.has(serviceName)) {
          serviceMap.set(serviceName, { count: 0, revenue: 0 });
        }
        
        const current = serviceMap.get(serviceName)!;
        current.count += 1;
        if (revenueEligibleStatuses.has(apt.status)) {
          current.revenue += price;
        }
      });

      const servicePopularity = Array.from(serviceMap.entries())
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Weekday trends (week + month)
      const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const weekdayIndex = (d: Date) => (d.getDay() + 6) % 7; // Mon=0..Sun=6

      // "Weekly" = cumulative by weekday across last 8 weeks (more meaningful than a single week)
      const weeklyCumulativeStart = subWeeks(now, 8).toISOString();
      const weekAppointments = (appointments || []).filter((a) => a.start_datetime >= weeklyCumulativeStart);
      const monthAppointments = (appointments || []).filter((a) => a.start_datetime >= monthStart && a.start_datetime <= monthEnd);

      const weekdayTrendsWeek = weekdayLabels.map((day, idx) => ({
        day,
        count:
          weekAppointments.filter((a) => weekdayIndex(new Date(a.start_datetime)) === idx).length || 0,
      }));

      // "Monthly" = last 6 months (by month)
      const months = eachMonthOfInterval({
        start: startOfMonth(subMonths(now, 5)),
        end: startOfMonth(now),
      });
      const monthlyTrends6Months = months.map((m) => {
        const mStart = startOfMonth(m).toISOString();
        const mEnd = endOfMonth(m).toISOString();
        const count =
          (appointments || []).filter((a) => a.start_datetime >= mStart && a.start_datetime <= mEnd)
            .length || 0;
        return { month: format(m, "MMM yyyy"), count };
      });

      // Status distribution
      const statusMap = new Map<string, number>();
      appointments?.forEach((apt) => {
        const status = apt.status || "unknown";
        statusMap.set(status, (statusMap.get(status) || 0) + 1);
      });

      const statusDistribution = Array.from(statusMap.entries())
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count);

      // Weekly trends (last 8 weeks)
      const last8Weeks = eachWeekOfInterval(
        { start: subWeeks(now, 7), end: now },
        { weekStartsOn: 1 }
      );

      const weeklyTrends = last8Weeks.map((week) => {
        const weekStartDate = startOfWeek(week, { weekStartsOn: 1 });
        const weekEndDate = endOfWeek(week, { weekStartsOn: 1 });
        const weekStart = weekStartDate.toISOString();
        const weekEnd = weekEndDate.toISOString();

        const weekAppointments = appointments?.filter(
          (a) => a.start_datetime >= weekStart && a.start_datetime <= weekEnd
        ) || [];

        const bookings = weekAppointments.length;
        const revenue = weekAppointments
          .filter((a) => a.status === "completed")
          .reduce((sum, a: any) => sum + displayAppointmentTotal(a), 0);

        return {
          week: format(weekStartDate, "MMM d"),
          bookings,
          revenue,
        };
      });

      // Fetch appointment add-ons for current month (for performance + exports)
      const monthAppointmentIds = monthAppointmentsAll.map((a) => a.id);
      const addonsByAppointment = new Map<string, { total: number; names: string[] }>();
      if (monthAppointmentIds.length > 0) {
        const { data: addonsRows, error: addonsErr } = await supabase
          .from("appointment_addons")
          .select("appointment_id,name,price_amount")
          .in("appointment_id", monthAppointmentIds.slice(0, 1000));
        if (addonsErr) throw addonsErr;
        (addonsRows || []).forEach((row: any) => {
          const id = row.appointment_id;
          if (!addonsByAppointment.has(id)) addonsByAppointment.set(id, { total: 0, names: [] });
          const cur = addonsByAppointment.get(id)!;
          cur.total += Number(row.price_amount || 0);
          if (row.name) cur.names.push(String(row.name));
        });
      }

      // Service performance (current month)
      const servicePerfMap = new Map<
        string,
        { name: string; bookings: number; completed: number; revenue: number; sumTotal: number }
      >();
      monthAppointmentsAll.forEach((a: any) => {
        const serviceId = a.service?.id || a.service_id || "unknown";
        const serviceName = a.service?.name || "Unknown";
        if (!servicePerfMap.has(serviceId)) {
          servicePerfMap.set(serviceId, { name: serviceName, bookings: 0, completed: 0, revenue: 0, sumTotal: 0 });
        }
        const cur = servicePerfMap.get(serviceId)!;
        cur.bookings += 1;
        const total = displayAppointmentTotal(a);
        cur.sumTotal += total;
        if (a.status === "completed") {
          cur.completed += 1;
        }
        if (revenueEligibleStatuses.has(a.status)) {
          cur.revenue += total;
        }
      });

      const servicePerformance = Array.from(servicePerfMap.entries())
        .map(([serviceId, v]) => ({
          serviceId,
          serviceName: v.name,
          bookings: v.bookings,
          revenue: v.revenue,
          avgPrice: v.bookings > 0 ? v.sumTotal / v.bookings : 0,
          completionRate: v.bookings > 0 ? (v.completed / v.bookings) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue);

      // Staff availability (weekly hours) for utilization estimate
      const { data: staffRows, error: staffErr } = await supabase
        .from("staff")
        .select("id,name,active")
        .eq("business_id", business.id);
      if (staffErr) throw staffErr;

      const staffIds = (staffRows || []).map((s: any) => s.id);
      const { data: staffHoursRows, error: staffHoursErr } = await supabase
        .from("staff_hours")
        .select("staff_id,day_of_week,start_time,end_time,is_off")
        .in("staff_id", staffIds.length ? staffIds : ["00000000-0000-0000-0000-000000000000"]);
      if (staffHoursErr) throw staffHoursErr;

      const weeklyAvailableHoursByStaff = new Map<string, number>();
      (staffRows || []).forEach((s: any) => weeklyAvailableHoursByStaff.set(s.id, 0));
      (staffHoursRows || []).forEach((h: any) => {
        if (h.is_off) return;
        const mins = Math.max(0, timeToMinutes(h.end_time) - timeToMinutes(h.start_time));
        weeklyAvailableHoursByStaff.set(
          h.staff_id,
          (weeklyAvailableHoursByStaff.get(h.staff_id) || 0) + mins / 60,
        );
      });

      const daysInMonth =
        (new Date(monthEnd).getTime() - new Date(monthStart).getTime()) / (1000 * 60 * 60 * 24) + 1;
      const approxWeeksInMonth = daysInMonth / 7;

      // Staff performance (current month)
      const staffPerfMap = new Map<
        string,
        {
          name: string;
          bookings: number;
          completed: number;
          noShow: number;
          revenue: number;
          sumTotal: number;
          hours: number;
          serviceCounts: Map<string, number>;
        }
      >();

      const ensureStaff = (id: string, name: string) => {
        if (!staffPerfMap.has(id)) {
          staffPerfMap.set(id, {
            name,
            bookings: 0,
            completed: 0,
            noShow: 0,
            revenue: 0,
            sumTotal: 0,
            hours: 0,
            serviceCounts: new Map<string, number>(),
          });
        }
        return staffPerfMap.get(id)!;
      };

      // Include all active staff so the table shows every provider (and their clock-ins) even with no appointments this month
      (staffRows || []).filter((s: any) => s.active !== false).forEach((s: any) => ensureStaff(s.id, s.name));

      monthAppointmentsAll.forEach((a: any) => {
        const sid = a.staff?.id || a.staff_id || "unassigned";
        const sname = a.staff?.name || "Unassigned";
        const cur = ensureStaff(sid, sname);
        cur.bookings += 1;
        if (a.status === "completed") cur.completed += 1;
        if (a.status === "no_show") cur.noShow += 1;
        const total = displayAppointmentTotal(a);
        cur.sumTotal += total;
        if (revenueEligibleStatuses.has(a.status)) cur.revenue += total;
        const durHours =
          a.end_datetime && a.start_datetime
            ? (new Date(a.end_datetime).getTime() - new Date(a.start_datetime).getTime()) / (1000 * 60 * 60)
            : 0;
        cur.hours += Math.max(0, durHours);
        const serviceName = a.service?.name || "Unknown";
        cur.serviceCounts.set(serviceName, (cur.serviceCounts.get(serviceName) || 0) + 1);
      });

      // Staff clock events this month (for Service Provider section): count, lastAt, and hours worked from clock_in/clock_out pairs
      const clockEventsByStaff = new Map<string, { count: number; lastAt: string | null; hoursWorked: number }>();
      try {
        const { data: clockRows } = await supabase
          .from("staff_clock_events")
          .select("staff_id, event_type, recorded_at")
          .eq("business_id", business.id)
          .gte("recorded_at", monthStart)
          .lte("recorded_at", monthEnd)
          .order("recorded_at", { ascending: true });
        const rows = (clockRows || []) as Array<{ staff_id: string; event_type: string; recorded_at: string }>;
        // Group by staff, then compute hours worked from consecutive clock_in -> clock_out pairs
        const byStaff = new Map<string, typeof rows>();
        rows.forEach((r) => {
          if (!byStaff.has(r.staff_id)) byStaff.set(r.staff_id, []);
          byStaff.get(r.staff_id)!.push(r);
        });
        byStaff.forEach((events, sid) => {
          let count = 0;
          let lastAt: string | null = null;
          let hoursWorked = 0;
          let lastClockIn: string | null = null;
          events.forEach((e) => {
            if (e.recorded_at && (!lastAt || e.recorded_at > lastAt)) lastAt = e.recorded_at;
            if (e.event_type === "clock_in") {
              count += 1;
              lastClockIn = e.recorded_at;
            } else if (e.event_type === "clock_out" && lastClockIn) {
              const inMs = new Date(lastClockIn).getTime();
              const outMs = new Date(e.recorded_at).getTime();
              hoursWorked += Math.max(0, (outMs - inMs) / (1000 * 60 * 60));
              lastClockIn = null;
            }
          });
          clockEventsByStaff.set(sid, { count, lastAt, hoursWorked });
        });
      } catch {
        // Table may not exist yet; ignore
      }

      const staffPerformance = Array.from(staffPerfMap.entries()).map(([staffId, v]) => {
        const specialties = Array.from(v.serviceCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name]) => name);
        const clock = clockEventsByStaff.get(staffId);
        return {
          staffId,
          staffName: v.name,
          bookings: v.bookings,
          revenue: v.revenue,
          completionRate: v.bookings > 0 ? (v.completed / v.bookings) * 100 : 0,
          hours: v.hours,
          hoursWorkedFromClock: clock?.hoursWorked ?? 0,
          specialties,
          clockInsCount: clock?.count ?? 0,
          lastClockAt: clock?.lastAt ?? null,
        };
      }).sort((a, b) => b.revenue - a.revenue);

      const activeProviders = (staffRows || []).filter((s: any) => s.active !== false).length;
      const top = staffPerformance.find((s) => s.staffId !== "unassigned") || null;
      const avgCompletionRate =
        staffPerformance.length > 0
          ? staffPerformance.reduce((sum, s) => sum + s.completionRate, 0) / staffPerformance.length
          : 0;

      const staffSummary = {
        activeProviders,
        topPerformerName: top?.staffName || null,
        topPerformerRevenue: top?.revenue || 0,
        avgCompletionRate,
      };

      // Export appointments (current month)
      const exportAppointments = monthAppointmentsAll.map((a: any) => {
        const addons = addonsByAppointment.get(a.id);
        const currency = a.currency || a.service?.currency || business.currency || "GHS";
        const total = netAppointmentTotal(a);
        return {
          booked_at: a.created_at || "",
          appointment_start: a.start_datetime,
          appointment_end: a.end_datetime,
          customer_name: a.customer?.name || "",
          customer_email: a.customer?.email || "",
          service_name: a.service?.name || "",
          staff_name: a.staff?.name || "Unassigned",
          status: a.status || "",
          total_price_amount: total,
          currency,
          addons: addons?.names?.join(" | ") || "",
          source: a.source || "",
          confirmation_code: a.confirmation_code || "",
        };
      });

      setData({
        bookingsThisWeek,
        bookingsThisMonth,
        revenueThisMonth,
        invoiceRevenueThisMonth,
        walkInRevenueThisMonth,
        membershipRevenueThisMonth,
        totalRevenueThisMonth,
        noShowRate: Math.round(noShowRate * 10) / 10,
        revenueOverTime,
        appointmentsOverTime,
        servicePopularity,
        statusDistribution,
        weeklyTrends,
        weekdayTrendsWeek,
        monthlyTrends6Months,
        servicePerformance,
        staffSummary,
        staffPerformance,
        exportAppointments,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  }, [business, selectedLocationId, categoryId, optionFromTime, optionToTime, optionFrom, optionTo]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    data,
    loading,
    error,
    refetch: fetchAnalytics,
    categories,
  };
}

